import { z } from 'zod';
/**
 * Printers, print agents, and print jobs.
 *
 * The app runs on Railway and the printers are on the shop LAN, so the server
 * cannot reach them: an agent on a shop PC connects outward and does the
 * printing. These schemas describe what an administrator sets up and what an
 * operator asks for.
 */
const trimmed = (max) => z.string().trim().min(1).max(max);
export const printerKindSchema = z.enum(['ZEBRA_NETWORK', 'WINDOWS_DRIVER']);
/**
 * A hostname or IP on the shop network.
 *
 * Deliberately permissive about *what* it addresses and strict about what it
 * cannot contain: no scheme, no path, no port (the port is its own field, so
 * `192.168.1.50:9100` here would silently produce `192.168.1.50:9100:9100`).
 */
const printerHost = z
    .string()
    .trim()
    .min(1)
    .max(255)
    .refine((value) => !/^[a-z]+:\/\//i.test(value), 'Just the address — no http:// in front')
    .refine((value) => !value.includes('/'), 'Just the address — no path')
    .refine((value) => !/:\d+$/.test(value), 'Put the port in the port field, not after the address');
export const printerInputSchema = z
    .object({
    name: trimmed(80),
    kind: printerKindSchema,
    host: z.union([printerHost, z.null()]).optional(),
    /**
     * 9100 is the raw-print port every Zebra listens on out of the box. Ports
     * below 1024 other than that are almost certainly a typo rather than a
     * deliberate choice, but they are not rejected — a shop with an unusual
     * print server is entitled to one.
     */
    port: z.coerce.number().int().min(1).max(65535).default(9100),
    windowsName: z.union([z.string().trim().min(1).max(200), z.null()]).optional(),
    isDefault: z.boolean().default(false),
    agentId: z.union([z.string().min(1), z.null()]).optional(),
})
    .superRefine((value, ctx) => {
    // Mirrors the printer_addressable CHECK constraint in the database. Both
    // exist on purpose: this one produces a message next to the right field,
    // the database one means no code path can create an unprintable printer.
    if (value.kind === 'ZEBRA_NETWORK' && !value.host) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['host'],
            message: 'A network Zebra needs its address on the shop network',
        });
    }
    if (value.kind === 'WINDOWS_DRIVER' && !value.windowsName) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['windowsName'],
            message: 'Enter the printer name exactly as Windows shows it',
        });
    }
});
export const printAgentInputSchema = z.object({
    name: trimmed(80),
});
/**
 * One press of Print.
 *
 * The 200-label ceiling is not arbitrary: it is roughly a full day's bulk run,
 * and a single job larger than that is more likely a mis-click on "select all"
 * than an intention. Copies is capped much lower for the same reason — a
 * reprint is one or two, and a typo that sends forty is expensive in stock.
 */
export const printJobInputSchema = z
    .object({
    printerId: z.string().min(1),
    parcelIds: z.array(z.string().min(1)).max(200).default([]),
    mailLabelIds: z.array(z.string().min(1)).max(200).default([]),
    copies: z.coerce.number().int().min(1).max(20).default(1),
})
    .refine((value) => value.parcelIds.length + value.mailLabelIds.length > 0, 'Select at least one label to print');
export const printJobStatusSchema = z.enum(['QUEUED', 'PRINTING', 'SENT', 'FAILED', 'CANCELLED']);
/** What an agent reports back after attempting a job. */
export const printJobResultSchema = z.object({
    // "sent", not "printed". Raw TCP has no acknowledgement and a Windows spooler
    // accepts a job long before any paper moves, so the agent can only ever
    // report that it handed the bytes over. An empty tray looks like success.
    ok: z.boolean(),
    error: z.union([z.string().trim().max(500), z.null()]).optional(),
});
//# sourceMappingURL=printing.js.map